const BASE_URL = 'https://api.dev.auxo.dk/public';

/**
 * Optimerer en billed-URL ved hjælp af Cloudflare on-the-fly resizing.
 * @param {string} url 
 * @param {object} options 
 * @returns {string}
 */
export function getOptimizedImageUrl(url, options = {}) {
  if (!url || !url.startsWith('https://assets.auxodev.dk')) {
    return url;
  }
  
  // Hvis URL'en allerede er optimeret, skal vi ikke dobbelt-optimere den
  if (url.includes('/cdn-cgi/image/')) {
    return url;
  }

  const { width = 800, quality = 80, format = 'auto', fit = 'cover', height } = options;
  const params = [`width=${width}`, `quality=${quality}`, `format=${format}`, `fit=${fit}`];
  if (height) {
    params.push(`height=${height}`);
  }

  return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/${params.join(',')}/`);
}

/**
 * Henter alle elementer fra en CMS samling ved hjælp af v2 endpointet (paged format).
 * @param {string} collectionName 
 * @param {object} params 
 * @returns {Promise<Array>}
 */
export async function fetchCollection(collectionName, params = {}) {
  try {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, val]) => {
      if (val !== undefined && val !== null) {
        query.append(key, val);
      }
    });
    
    const url = `${BASE_URL}/v2/content/${collectionName}?${query.toString()}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch collection ${collectionName}`);
    }
    const data = await response.json();
    return data.items || [];
  } catch (error) {
    console.error(`Error fetching collection ${collectionName}:`, error);
    return [];
  }
}

/**
 * Henter en enkelt side baseret på dens url_slug.
 * @param {string} urlSlug 
 * @returns {Promise<object|null>}
 */
export async function fetchPageBySlug(urlSlug) {
  try {
    const pages = await fetchCollection('pages', { limit: 100 });
    const page = pages.find(p => p.url_slug === urlSlug);
    return page || null;
  } catch (error) {
    console.error(`Error fetching page by slug ${urlSlug}:`, error);
    return null;
  }
}

/**
 * Henter en enkelt case baseret på dens url_slug.
 * @param {string} urlSlug 
 * @returns {Promise<object|null>}
 */
export async function fetchCaseBySlug(urlSlug) {
  try {
    const cases = await fetchCollection('cases', { limit: 100 });
    const item = cases.find(c => c.url_slug === urlSlug);
    return item || null;
  } catch (error) {
    console.error(`Error fetching case by slug ${urlSlug}:`, error);
    return null;
  }
}

/**
 * Henter en enkelt nyhed baseret på dens url_slug.
 * @param {string} urlSlug 
 * @returns {Promise<object|null>}
 */
export async function fetchNewsBySlug(urlSlug) {
  try {
    const news = await fetchCollection('news', { limit: 100 });
    const item = news.find(n => n.url_slug === urlSlug);
    return item || null;
  } catch (error) {
    console.error(`Error fetching news by slug ${urlSlug}:`, error);
    return null;
  }
}

/**
 * Henter et galleri baseret på dets gallery_slug.
 * @param {string} gallerySlug 
 * @returns {Promise<object|null>}
 */
export async function fetchGalleryBySlug(gallerySlug) {
  try {
    const galleries = await fetchCollection('galleries', { limit: 100 });
    const gallery = galleries.find(g => g.gallery_slug === gallerySlug);
    return gallery || null;
  } catch (error) {
    console.error(`Error fetching gallery by slug ${gallerySlug}:`, error);
    return null;
  }
}
